import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types';
import { keyCode, open, close } from '../../helpers/index';
import Axios from 'axios';

const SearchBar = ({
  searchLabel,
  onClear,
  onSearch,
  language,
  initialQuery,
  endpointSuggestions
}) => {
  const [query, setQuery] = useState(initialQuery || '');
  const component = useRef(null);
  const searchForm = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const suggestionsList = useRef(null);

  useEffect(() => {
    if (suggestionsList.current) {
      if (suggestionsOpen) {
        open({ element: suggestionsList.current });
        suggestionsList.current.style.display = 'block';
      } else {
        close({ element: suggestionsList.current });
        suggestionsList.current.style.display = 'none';
      }
    }
  }, [suggestionsList, suggestionsOpen]);

  useEffect(() => {
    if (initialQuery) {
      setQuery(initialQuery);
    }
  }, [initialQuery]);

  useEffect(() => {
    if (suggestionsList.current && selectedSuggestion !== null) {
      const list = suggestionsList.current;
      const selected = list.querySelector('[aria-selected="true"');
      const position = list.offsetHeight - list.scrollTop;

      if (selected && selected.offsetTop > list.offsetHeight) {
        list.scrollTop =
          selected.offsetTop - list.offsetHeight + selected.offsetHeight;
      } else if (list.scrollTop !== 0) {
        list.scrollTop = 0;
      }
    }
  }, [suggestionsList, selectedSuggestion]);

  useEffect(() => {
    const handleClickOutside = e => {
      const container = component.current;

      if (container) {
        if (container !== e.target && !container.contains(e.target)) {
          setSuggestionsOpen(false);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (query.length > 0) {
      searchForm.current.classList.add('is-filled');
    } else {
      searchForm.current.classList.remove('is-filled');
    }
  }, [query]);

  const handleTextChange = e => {
    e.preventDefault();
    const value = e.target.value;
    handleLoadSuggestions(value);
    setQuery(value);
  };

  const handleSubmit = (e, item) => {
    e.preventDefault();
    onSearch(item || query);
    setSuggestionsOpen(false);
  };

  const handleClear = () => {
    setQuery('');
    searchForm.current.classList.remove('is-focused');
    searchForm.current.classList.remove('is-filled');
    setSuggestions([]);
    onClear();
  };

  const handleFocus = () => {
    searchForm.current.classList.add('is-focused');
  };

  const handleBlur = () => {
    searchForm.current.classList.remove('is-focused');
  };

  const handleLoadSuggestions = async value => {
    if (value.length > 2) {
      const { data } = await Axios({
        url: endpointSuggestions,
        params: { q: value, language }
      });
      setSuggestions(data.suggestions);

      if (data.suggestions.length) {
        setSuggestionsOpen(true);
      } else {
        setSuggestionsOpen(false);
      }
    } else {
      setSuggestions(null);
      setSuggestionsOpen(false);
    }
  };

  const handleKeyDown = e => {
    const key = e.which || e.keyCode;
    let selected = null;

    if (!suggestions) return;

    switch (key) {
      case keyCode.UP:
        e.preventDefault();
        if (selectedSuggestion !== null && selectedSuggestion > 0) {
          selected = selectedSuggestion - 1;
        } else {
          selected = suggestions.length - 1;
        }
        setQuery(suggestions[selected]);
        setSelectedSuggestion(selected);
        break;

      case keyCode.DOWN:
        e.preventDefault();

        if (
          selectedSuggestion !== null &&
          selectedSuggestion < suggestions.length - 1
        ) {
          selected = selectedSuggestion + 1;
        } else {
          selected = 0;
        }

        setQuery(suggestions[selected]);
        setSelectedSuggestion(selected);
        break;

      case keyCode.RETURN:
        setSuggestions([]);
        setSuggestionsOpen(false);
        break;

      case keyCode.ESC:
        setQuery('');
        setSuggestionsOpen(false);
        break;
    }
  };

  const handleInputFocus = e => {
    if (query.length > 2 && suggestions.length) {
      setSuggestionsOpen(true);
    }
  };

  const handleSelectedOption = (e, item) => {
    e.preventDefault();

    setQuery(item);
    setSuggestions([]);
    setSuggestionsOpen(false);
    handleSubmit(e, item);
  };

  return (
    <div
      className="search"
      aria-owns="options-list"
      ref={component}
    >
      <form autoComplete="off" className="search__form" onSubmit={handleSubmit} ref={searchForm}>
        <label className="search__label">{searchLabel}</label>
        <input
          type="text"
          name="q"
          value={query}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className="search__input"
          aria-label="Search Box"
          autoComplete="off"
        />
        {!!query && (
          <button
            type="button"
            className="search__clear"
            style={{ display: 'block' }}
            onClick={handleClear}
          >
            <svg viewBox="0 0 60 60">
              <use xlinkHref="#close-icon-rebrand" />
            </svg>
          </button>
        )}
      </form>
      {suggestions && (
        <ul
          id="options-list"
          className="search-results__suggestions-list"
          ref={suggestionsList}
        >
          {suggestions.map((item, index) => (
            <li
              key={index}
              className="search-results__suggestions-item"
              role="option"
              aria-selected={selectedSuggestion === index}
              onClick={e => handleSelectedOption(e, item)}
            >
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

SearchBar.propTypes = {
  initialQuery: PropTypes.string,
  searchLabel: PropTypes.string.isRequired,
  onClear: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired
};

export default SearchBar;
